// frontend/src/components/DonateModal.jsx
import { useState } from 'react';
import { API_BASE } from '../api';

const DonateModal = ({ onClose }) => {
  const [donation, setDonation] = useState({
    name: '',
    whatsapp: '',
    email: '',
    city: '',
    amount: '',
    note: '',
    aadharCard: '',
    panCard: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setDonation({ ...donation, [e.target.name]: e.target.value });
  };

  const handleAmountSelect = (amt) => {
    setDonation({ ...donation, amount: amt });
  };

  const handleDonate = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Step 1: Call backend to create donation record & get PayU params
      const res = await fetch(`${API_BASE}/api/donations/initiate-payment`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(donation),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || 'Failed to initiate payment');
      }

      const { payuParams, payuUrl } = data;

      // Step 2: Build a hidden HTML form and auto-submit it to PayU
      // This redirects the entire browser to the PayU checkout page.
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = payuUrl;

      Object.entries(payuParams).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value ?? '';
        form.appendChild(input);
      });

      document.body.appendChild(form);
      form.submit();
      // Browser navigates away — modal state doesn't matter after this point
    } catch (err) {
      console.error('Donation initiation error:', err);
      setError(err.message || 'Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  const defaultAmounts = ['100', '200', '500', '1000', '2000'];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 max-h-screen overflow-y-auto">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-primary">Donate Now</h2>
          <button
            onClick={onClose}
            className="text-gray-600 hover:text-gray-900 text-2xl leading-none"
            aria-label="Close"
          >
            &times;
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleDonate} className="space-y-4">
          <div>
            <label className="block text-sm font-semibold text-dark">Name*</label>
            <input
              type="text"
              name="name"
              value={donation.name}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-dark">WhatsApp Number*</label>
            <input
              type="text"
              name="whatsapp"
              value={donation.whatsapp}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-dark">City*</label>
            <input
              type="text"
              name="city"
              value={donation.city}
              onChange={handleChange}
              required
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-dark">Email (optional)</label>
            <input
              type="email"
              name="email"
              value={donation.email}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-dark">Aadhar Card Number (optional)</label>
            <input
              type="text"
              name="aadharCard"
              value={donation.aadharCard}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-dark">PAN Card Number (optional)</label>
            <input
              type="text"
              name="panCard"
              value={donation.panCard}
              onChange={handleChange}
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-dark">Amount (₹)*</label>
            <div className="flex flex-wrap gap-2 mb-2">
              {defaultAmounts.map((amt) => (
                <button
                  key={amt}
                  type="button"
                  onClick={() => handleAmountSelect(amt)}
                  className={`border rounded px-3 py-1 transition-colors ${
                    donation.amount === amt
                      ? 'bg-primary text-white'
                      : 'bg-white text-dark hover:bg-gray-200'
                  }`}
                >
                  ₹{amt}
                </button>
              ))}
            </div>
            <input
              type="number"
              name="amount"
              placeholder="Or enter custom amount"
              value={donation.amount}
              onChange={handleChange}
              required
              min="1"
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:border-primary"
            />
          </div>

          <div>
            <label className="block text-sm font-semibold text-dark">Note (optional)</label>
            <textarea
              name="note"
              value={donation.note}
              onChange={handleChange}
              rows="2"
              className="w-full border border-gray-300 p-2 rounded focus:outline-none focus:border-primary"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-primary text-white py-2 rounded hover:bg-secondary transition-colors disabled:opacity-60"
          >
            {loading ? 'Redirecting to PayU...' : 'Proceed to Donate'}
          </button>

          <p className="text-xs text-gray-500 text-center">
            You will be securely redirected to PayU to complete payment.
          </p>
        </form>
      </div>
    </div>
  );
};

export default DonateModal;
